import React, { useEffect,useRef,useState } from 'react';
import { Mail,User,LockKeyhole,ArrowLeft } from 'lucide-react';
import { useCareerLanguage } from './CareerLanguage';
import {AuthShell,AuthField,AuthSubmit,AuthMessage,authError,authValidation,focusAuthError} from './CareerAuth';
import {EMAIL_RESEND_SECONDS,emailWaitSeconds,emailWaitLabel,emailCooldownResponse,readAuthResponse,useEmailResendWait} from './CareerEmailVerification';
import {useCareerPrivacyNotice,CareerRegistrationNotice} from './CareerPrivacyNotice';
import {upperCareerName} from './careerNames';
import {registrationPasswordErrors,registrationFieldErrors} from './registrationErrors';
export default function CareerRegister({
  onClose,
  openLoginPopup = onClose,
  email = '',
  onSuccess,
  resendAfterSeconds=0,
  resumeVerification=false
}) {
  const {
    t,
    lang
  } = useCareerLanguage();
  const [form, setForm] = useState({
      adi: '',
      soyadi: '',
      email:email.trim(),
      password: '',
      confirm: ''
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [verify, setVerify] = useState(!!email),
    [code, setCode] = useState(''),
    [fieldErrors,setFieldErrors]=useState({}),[message,setMessage]=useState('');
  const busyRef=useRef(false),pendingFocus=useRef(null);
  useEffect(()=>{if(!busy&&pendingFocus.current){focusAuthError(pendingFocus.current,'ca-register-');pendingFocus.current=null;}},[busy]);
  const resendWait=useEmailResendWait(email?resendAfterSeconds:0);
  const privacy=useCareerPrivacyNotice(!verify);
  const edit=(key,value)=>{
    const next={...form,[key]:key==='adi'||key==='soyadi'?upperCareerName(value):value};setForm(next);
    setFieldErrors(x=>{const missing=key==='password'&&x.password?registrationPasswordErrors(value,t):[];return {...x,[key]:missing.length?missing:'',...((key==='password'||key==='confirm')&&x.confirm?{confirm:next.confirm&&next.password!==next.confirm?t('authPasswordsMismatch'):''}:{})};});setError('');
  };
  const validateConfirm=()=>setFieldErrors(x=>({...x,confirm:authValidation(form,['confirm'],t).confirm||''}));
  async function submit(e) {
    e.preventDefault();
    if (busyRef.current) return;
    const keys=verify?['code']:['adi','soyadi','email','password','confirm'];
    const validation=authValidation(verify?{code}:form,keys,t);
    if(!verify&&form.password){const missing=registrationPasswordErrors(form.password,t);if(missing.length)validation.password=missing;}
    const issues=Object.fromEntries(keys.filter(key=>validation[key]).map(key=>[key,validation[key]]));
    setFieldErrors(issues);if(Object.keys(issues).length){focusAuthError(issues,'ca-register-');return;}
    if(!verify&&!privacy.notice){setError(t(privacy.loading?'loading':'privacyPending'));return;}
    busyRef.current=true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const url = verify ? '/api/Auth/ConfirmEmailWithCode?' + new URLSearchParams({
        email: form.email,
        code
      }) : '/api/Auth/register';
      const r = await fetch(process.env.REACT_APP_API_BASE_URL + url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        ...(!verify ? {
          body: JSON.stringify({
            adi:upperCareerName(form.adi).trim(),soyadi:upperCareerName(form.soyadi).trim(),email:form.email.trim(),password:form.password,
            dil: lang
          })
        } : {})
      });
      const {text,data}=await readAuthResponse(r);
      if (!r.ok) {
        const fields=verify?{}:registrationFieldErrors(data,text,t,r.status);
        if(Object.keys(fields).length){setFieldErrors(fields);pendingFocus.current=fields;}
        else setError(await authError({status:r.status,text:async()=>text},t,verify?'authInvalidCode':'authRegistrationFailed'));
        return;
      }
      if (verify) {
        if (onSuccess) onSuccess();else openLoginPopup(form.email);
      } else {
        setVerify(true);
        resendWait.start(emailWaitSeconds(data,EMAIL_RESEND_SECONDS));
        setForm(x => ({
          ...x,
          password: '',
          confirm: ''
        }));
      }
    } catch {
      setError(t('authNetworkError'));
    } finally {
      busyRef.current=false;
      setBusy(false);
    }
  }
  async function resend() {
    if (busyRef.current||resendWait.remaining()>0) return;
    busyRef.current=true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/Auth/ResendEmailCode?' + new URLSearchParams({
        email: form.email
      }), {
        method: 'POST'
      });
      const {text,data}=await readAuthResponse(r);
      if (!r.ok){
        if(emailCooldownResponse(data,text)){
          resendWait.start(emailWaitSeconds(data,EMAIL_RESEND_SECONDS));setError(t('authResendWait'));
        }else setError(await authError({status:r.status,text:async()=>text},t));
        return;
      }
      resendWait.start(emailWaitSeconds(data,EMAIL_RESEND_SECONDS));setMessage(t('authCodeSent'));
    } catch {
      setError(t('authNetworkError'));
    } finally {
      busyRef.current=false;
      setBusy(false);
    }
  }
  return <AuthShell title={t(verify?'emailCode':'register')} intro={t(verify?'authVerifyIntro':'authRegisterIntro')} busy={busy} onClose={onClose} mode={verify?undefined:'register'} onLogin={()=>openLoginPopup(form.email)} onRegister={()=>{}} focusKey={verify} steps={{labels:[t('authAccountStage'),t('authCodeStage')],active:verify?1:0}}>
   {verify&&resumeVerification&&<p className="ca-feedback is-success ca-resume-note">{t('authVerifyResume')}</p>}
   {verify&&<div className="ca-mail-address"><Mail size={18} aria-hidden="true"/><span>{form.email.trim()}</span></div>}
   {!verify&&<CareerRegistrationNotice policy={privacy}/>}
   <form className="ca-form" onSubmit={submit} noValidate aria-busy={busy}>
    <fieldset className="ca-form-fields" disabled={busy}>
    {verify?<AuthField id="ca-register-code" label={t('code')} className="ca-code-field" required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} placeholder="000000" value={code} error={fieldErrors.code} data-auth-autofocus onChange={e=>{setCode(e.target.value.replace(/\D/g,'').slice(0,6));setFieldErrors({});setError('');}}/>:<>
     <div className="ca-name-grid">{[['adi','firstName','given-name'],['soyadi','lastName','family-name']].map(([key,label,autoComplete])=><AuthField key={key} id={'ca-register-'+key} label={t(label)} icon={User} required autoComplete={autoComplete} maxLength={100} value={form[key]} error={fieldErrors[key]} data-auth-autofocus={key==='adi'?true:undefined} onChange={e=>edit(key,e.target.value)}/>)}</div>
     <AuthField id="ca-register-email" label={t('email')} icon={Mail} type="email" required autoComplete="email" maxLength={256} placeholder="name@example.com" value={form.email} error={fieldErrors.email} onChange={e=>edit('email',e.target.value)}/>
     <AuthField id="ca-register-password" label={t('password')} icon={LockKeyhole} password required autoComplete="new-password" minLength={6} maxLength={128} value={form.password} hint={t('passwordNote')} error={fieldErrors.password} onChange={e=>edit('password',e.target.value)} onBlur={()=>{if(form.password){const missing=registrationPasswordErrors(form.password,t);setFieldErrors(x=>({...x,password:missing.length?missing:''}));}if(form.confirm)validateConfirm();}}/>
     <AuthField id="ca-register-confirm" label={t('authConfirmPassword')} icon={LockKeyhole} password required autoComplete="new-password" maxLength={128} value={form.confirm} error={fieldErrors.confirm} onChange={e=>edit('confirm',e.target.value)} onBlur={validateConfirm}/>
    </>}
    </fieldset><AuthMessage error={error} message={message}/>
    <AuthSubmit busy={busy} disabled={!verify&&!privacy.notice} label={t(verify?'verify':'register')}/>
    {verify&&<><p className="ca-helper">{t('authEmailValidity')}</p><div className="ca-resend"><button type="button" className="ca-link" disabled={busy||resendWait.seconds>0} onClick={resend}>{t('authResendCode')}{resendWait.seconds>0?' · '+emailWaitLabel(resendWait.seconds):''}</button></div></>}
   </form>
   <footer className="ca-footer">{verify?<button type="button" className="ca-link" disabled={busy} onClick={()=>openLoginPopup(form.email)}><ArrowLeft size={14}/>{t('authBackLogin')}</button>:<><span>{t('authHaveAccount')}</span><button type="button" className="ca-link" disabled={busy} onClick={()=>openLoginPopup(form.email)}>{t('login')}</button></>}</footer>
  </AuthShell>;
}
